import { GlobalComponentsManager } from "@nectary/components/utils/global-components-manager";
import { getFeatureFlag } from "@sinch/feature-flags";
import { MFECatalog } from "@sinch/mfe-loader";
import { Provider } from "jotai";
import { useState, useEffect, Suspense } from "react";
import { StyleSheetManager } from "styled-components";
import { ConditionalWrapper } from "./components/ConditionalWrapper";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { MFELoaderWrapper } from "./components/MFELoaderWrapper";
import { Plugins } from "./components/Plugins";
import type { Config } from "./config";
import { globalJotaiStore } from "./jotai-store";
import { getBaseMfeConfig } from "./mfe/mfeConfig";
import { getRenderShellOptions } from "./portals";
import { init as initSentry, setupInitialTraces, traceManager } from "./sentry";
import { styleDeduplicator } from "./style-deduplicator";
import { getDeviceType, getNetworkConnection } from "./utils";

const ShellDevtools = () => {
  const [_, ENV] = window.SHELL_ENV.CONFIG_NAME.split(".");
  const isNotProd =
    ENV === "stg" || ENV === "dev" || ENV === "local" || ENV === "qa";
  if (isNotProd) {
    return (
      <MFELoaderWrapper
        name="Devtools"
        basePath="/devtools"
        Fallback={() => <></>}
      />
    );
  }
  return null;
};

interface RenderShellOpts {
  config: Config;
  shadowRoot?: ShadowRoot | null;
}

export const ShellRenderer = ({ config, shadowRoot }: RenderShellOpts) => {
  const [isReady, setIsReady] = useState(false);

  const { App: App, Loader: LoaderAnimation } = getRenderShellOptions(config);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: Expression produces a union type that is too complex to represent
  const appElement = <App />;

  useEffect(() => {
    (async () => {
      console.log("APP_VERSION:", `shell/${COMMIT_HASH}`);
      try {
        initSentry(config);

        const transaction = traceManager.startTransaction(
          "TimeToFirstMFELoad",
          {
            startTime: performance.timeOrigin,
            attributes: {
              device_type: getDeviceType(),
              effective_connection_type: getNetworkConnection()?.effectiveType,
            },
          }
        );
        setupInitialTraces(transaction);

        let trace = traceManager.startTransaction("UniversalBootstrap", {
          parentSpan: transaction,
        });
        const { universalBootstrap } = await import("./universalBootstrap");
        await universalBootstrap(config);
        trace.end();

        styleDeduplicator.start();

        const baseMfeConfig = getBaseMfeConfig();

        const botBuilderMfeUrl = getFeatureFlag(
          "mmBotBuilderMfeUrl",
          (x): x is string => typeof x === "string",
          "https://app.chatbots.messagemedia.com"
        );

        const botBuilderMfeIsESM = getFeatureFlag(
          "mmBotBuilderMfeIsESM",
          (x): x is boolean => typeof x === "boolean",
          false
        );

        const extendedMfeConfig = {
          before: {
            ...baseMfeConfig,
          },
          after: {
            BotBuilderV2: {
              url: botBuilderMfeUrl,
              flags: botBuilderMfeIsESM ? ["esm"] : [],
            },
          },
        };
        MFECatalog.extendMfeConfig(extendedMfeConfig);

        try {
          trace = transaction.start("await-global-components");
          await Promise.race([
            GlobalComponentsManager.whenLoaded(),
            new Promise((resolve) => setTimeout(resolve, 2000)),
          ]);
        } catch (_) {
          //
        } finally {
          trace.end();
          setIsReady(true);
        }
      } catch (err) {
        const { loadCrashPage } = await import("./loadCrashPage");
        console.error(err);
        await loadCrashPage(err);
      }
    })();

    return () => {
      styleDeduplicator.stop();
    };
  }, [config]);

  return (
    <ConditionalWrapper
      shouldWrap={!!shadowRoot}
      wrapper={(children) => (
        <StyleSheetManager target={shadowRoot ?? undefined}>
          {children}
        </StyleSheetManager>
      )}
    >
      <ErrorBoundary>
        <Provider store={globalJotaiStore}>
          {isReady ? (
            <Suspense fallback={LoaderAnimation}>
              {appElement}
              <ShellDevtools />
              <Plugins />
            </Suspense>
          ) : (
            <LoaderAnimation />
          )}
        </Provider>
      </ErrorBoundary>
    </ConditionalWrapper>
  );
};
